import { NgClass } from '@angular/common';
import { Component, computed, effect, ElementRef, forwardRef, inject, input, InputSignal, model, ModelSignal, output, OutputEmitterRef, Signal, untracked, viewChild, Injector } from '@angular/core';
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR, ValidationErrors, NgControl } from '@angular/forms';
import { NgxMaskDirective, provideNgxMask } from 'ngx-mask';
import { SkeletonLoaderComponent } from "@shared/components/ui/skeleton-loader";
import { TranslateModule } from '@ngx-translate/core';
import { FormattingService } from '@settings/data-access';
import { SystemIconsComponent } from "@shared/components/ui/system-icons";

export type TType =
  | 'text'
  | 'password'
  | 'email'
  | 'currency'
  | 'currency-without-symbol'
  | 'number'
  | 'date';
export type TState = 'default' | 'disabled' | 'error' | 'success';
export type TScreen = 'desktop' | 'mobile';
export type TIconAlign = 'left' | 'right';
export type TStyleType = 'default' | 'ghost' | 'ghost-modal' | 'comboBoxDate';
export type TInputAlignment = 'left' | 'right' | 'center';

/**
 * The `InputComponent` is a standalone Angular component that provides an input field.
 * It uses Angular's reactive signals and effects to manage the state and value of the input.
 * It also implements the `ControlValueAccessor` interface to enable two-way data binding.
 */
@Component({
  selector: 'lib-input',
  templateUrl: './input.component.html',
  styleUrls: ['./input.component.scss'],
  standalone: true,
  imports: [
    NgClass,
    NgxMaskDirective,
    FormsModule,
    SkeletonLoaderComponent,
    TranslateModule,
    SystemIconsComponent
],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => InputComponent),
      multi: true,
    },
    provideNgxMask(),
  ],
})
export class InputComponent implements ControlValueAccessor {
  /**
   * Service for formatting values, such as currency symbols, based on user settings.
   */
  public readonly formattingService = inject(FormattingService);
  private injector = inject(Injector);

  /**
   * The suffix of the input.
   * @default ''
   */
  public suffix: InputSignal<string> = input<string>('');

  /**
   * The mask suffix of the input.
   * @default ''
   */
  public maskSuffix: InputSignal<string> = input<string>('');

  /**
   * The unique identifier of the input.
   * @default null
   */
  public readonly id: InputSignal<string> = input<string>();

  /**
   * The icon suffix of the input.
   * @default ''
   */
  public iconSuffix: InputSignal<string> = input<string>('');

  /**
   * The text alignment of the input.
   * @default 'text'
   */
  public alignment: InputSignal<TInputAlignment> = input<TInputAlignment>('left');

  /**
   * A flag indicating if comboBoxDate is open.
   * @default false
   */
  public readonly isComboBoxDateOpen: InputSignal<boolean> = input<boolean>(false);

  /**
   * The icon color of the input.
   * @default ''
   */
  public iconColor: InputSignal<string> = input<string>('');

  /**
   * The prefix of the input.
   * @default ''
   */
  public prefix: InputSignal<string> = input<string>(null);

  /**
   * The prefix mode of the input, determining how the prefix is displayed.
   * Currently supports 'currency' mode, which displays a currency symbol based on the user's settings.
   * @default null
   */
  public currencyPrefix: InputSignal<boolean> = input<boolean>(false);

  /**
   * The icon prefix of the input.
   * @default ''
   */
  public iconPrefix: InputSignal<string> = input<string>('');
  /**
   * The svg prefix of the input.
   * @default ''
   */
  public svgPrefix: InputSignal<string> = input<string>('');
  /**
   * A flag indicating if the input background is transaparent.
   * @default false
   */
  public readonly transparent: InputSignal<boolean> = input<boolean>(false);

  /**
   * A flag indicating if the input has linethrough.
   * @default false
   */
  public readonly lineThrough: InputSignal<boolean> = input<boolean>(false);

  /**
   * The value of the input.
   * @default ''
   */
  public readonly value: ModelSignal<string> = model<string>('');

  /**
   * The font token of the input.
   * @default 'content-regular'
   */
  public readonly fontToken: InputSignal<string> = input<string>('content-regular !font-open-sans');

  /**
   * The placeholder of the input.
   * @default ''
   */
  public readonly placeholder: InputSignal<string> = input<string>('');

  /**
   * The type of the input.
   * @default 'text'
   * @type 'text' | 'password' | 'email' | 'currency'
   */
  public readonly type: InputSignal<TType> = input<TType>('text');

  /**
   * The style type of the input.
   * @default 'default'
   * @type 'default' | 'ghost'
   */
  public readonly styleType: InputSignal<TStyleType> = input<TStyleType>('default');

  /**
   * A flag indicating if the input is required.
   * @default false
   */
  public readonly required: InputSignal<boolean> = input<boolean>();

      /**
   * The state of the input.
   * @default 'default'
   * @type 'default' | 'disabled'
   */
  public readonly state: ModelSignal<TState> = model<TState>('default');

    /**
   * Computed state that automatically detects error state from FormControl.
   * Maintains backward compatibility: only uses auto-detection when state is 'default'.
   */
  public readonly effectiveState = computed(() => {
    if (this.state() !== 'default') {
      return this.state();
    }

    try {
      const ngControl = this.injector.get(NgControl, null);

      if (ngControl?.control) {
        const control = ngControl.control;

        if (control.disabled) {
          return 'disabled';
        }

        if (control.invalid && (control.dirty || control.touched)) {
          return 'error';
        }
      }
    } catch {
      // If NgControl cannot be obtained, fallback to default state
    }

    return 'default';
  });

  /**
   * The screen of the input.
   * @default 'desktop'
   * @type 'desktop' | 'mobile'
   */
  public readonly screen: InputSignal<TScreen> = input<TScreen>('desktop');

  /**
   * The maximum length of the input.
   */
  public readonly maxLength: InputSignal<number | null> = input<number | null>();

  /**
   * The minimum length of the input.
   */
  public readonly minLength: InputSignal<number | null> = input<number | null>();

  /**
   * A flag indicating if the input is read-only.
   * @default false
   */
  public readonly readOnly: InputSignal<boolean> = input<boolean>(false);

  /**
   * The autocomplete value of the input.
   * @default ''
   */
  public readonly autocomplete: InputSignal<string> = input<string>();

  /**
   * Hint to the mobile OS about the type of virtual keyboard to show.
   * Use `'numeric'` for digits, `'tel'` for phone numbers, `'email'`, etc.
   * Critical for OTP / verification-code fields so the right keyboard appears.
   */
  public readonly inputmode: InputSignal<
    'none' | 'text' | 'decimal' | 'numeric' | 'tel' | 'search' | 'email' | 'url' | undefined
  > = input<'none' | 'text' | 'decimal' | 'numeric' | 'tel' | 'search' | 'email' | 'url'>();

  /**
   * Mobile auto-capitalize hint. Use `'characters'` for uppercase-only fields
   * (e.g. activation codes) so the on-screen keyboard opens in caps mode.
   */
  public readonly autocapitalize: InputSignal<
    'off' | 'none' | 'on' | 'sentences' | 'words' | 'characters' | undefined
  > = input<'off' | 'none' | 'on' | 'sentences' | 'words' | 'characters'>();

  /**
   * iOS Safari auto-correct hint. Disable for codes / identifiers so the
   * keyboard does not rewrite single-character entries.
   */
  public readonly autocorrect: InputSignal<'on' | 'off' | undefined> = input<'on' | 'off'>();

  /**
   * Enables/disables the browser's spell checker. Disable on code/id fields
   * to avoid red underlines and predictive-text interference (Android).
   */
  public readonly spellcheck: InputSignal<boolean | undefined> = input<boolean>();

  /**
   * The icon name from the icon library.
   * @default ''
   * @example 'rr-calendar-pen'
   */
  public readonly icon: InputSignal<string> = input<string>();

    /**
   * The icon name from the system-icon library.
   * @default ''
   * @example 'rr-calendar-pen'
   */
    public readonly systemIcon: InputSignal<string> = input<string>();

  /**
   * The icon align.
   * @default 'left'
   * @type 'left' | 'right'
   */
  public readonly iconAlign: InputSignal<TIconAlign> = input<TIconAlign>('left');

  /**
   * A flag indicating if the icon is clickable.
   * @default false
   */
  public readonly clickableIcon: InputSignal<boolean> = input<boolean>(false);

  /**
   * The output event emitter for the icon click.
   */
  public readonly iconClicked: OutputEmitterRef<void> = output<void>();

  /**
   * A flag indicating if the input is square, for example: Code verification.
   * @default false
   */
  public readonly squareInput: InputSignal<boolean> = input<boolean>(false);

  /**
   * The output event emitter for the blur event.
   * @event blur
   */
  public readonly blurChange: OutputEmitterRef<void> = output<void>();

  /**
   * The mask of the input.
   * @default ''
   * @example 000x00'
   */
  public readonly mask: InputSignal<string> = input<string>('');

  /**
   * The custom patterns for the input mask.
   */
  public readonly patterns: InputSignal<Record<string, { pattern: RegExp, optional?: boolean, symbol?: string }>> = input<Record<string, { pattern: RegExp, optional?: boolean, symbol?: string }>>();

  /**
   * The special characters of the input mask.
   * @example ['x']
   */
  public readonly specialCharacters: InputSignal<string[]> = input<string[]>([`-`, `/`, `(`, `)`, `.`, `:`, ` `, `+`, `,`, `@`, `[`, `]`, `"`, `'`]);

  /**
   * If the leadZeroDateTime parameter is true, skipped symbols of date or time will be replaced by 0. Default value is false.
   * @default false
   */
  public readonly leadZeroDateTime: InputSignal<boolean> = input<boolean>(false);

  /**
   * A flag indicating if the special characters should be dropped.
   * @default false
   */
  public readonly dropSpecialCharacters: InputSignal<boolean> = input<boolean>(true);

  /**
   * A flag indicating if the border should be shown.
   * @default true
   */
  public readonly withBorder: InputSignal<boolean> = input<boolean>(true);

  /**
   *  A flag indicating if the mask should be shown.
   */
  public readonly showMask: InputSignal<boolean> = input(false);

  /**
   * The input element reference.
   */
  public readonly inputElement: Signal<ElementRef<HTMLInputElement>> = viewChild<ElementRef<HTMLInputElement>>('inputElement');

  public readonly skeleton: InputSignal<boolean> = input<boolean>(false);

  /**
   * A flag indicating if the input is fit-content width.
   * @default false
   */
  public readonly fitContent: InputSignal<boolean> = input<boolean>(false);

  /**
   * The errors of the input.
   */
  public errors: ValidationErrors | null = null;

  /**
   * Internal formatted value for display purposes (used only for currency type).
   * @private
   */
  private formattedValue = '';

  /**
   * The function to be called when the input changes.
   */
  public onChange: any = () => {};

  /**
   * The function to be called when the input is touched.
   */
  public onTouched: any = () => {};

  /**
   * Formats a value as Brazilian Real currency using native Intl.NumberFormat API.
   * @param value The numeric value to format.
   * @returns The formatted currency string.
   */
  public formatAsCurrency(value: string | number): string {
    if (this.type() === 'currency-without-symbol') {
      return this.formattingService.formatNumber(value, 2)
    }

    return this.formattingService.formatCurrency(value);
  }

  public onValueChanged = effect(() => {
    this.value()
      untracked(() => {
        if (this.type() === 'currency' || this.type() === 'currency-without-symbol') {
          this.formattedValue = this.value() ? this.formatAsCurrency(this.value()) : null;
        }
      })
  })

  /**
   * Gets the display value (formatted for currency, original for others).
   * @returns The display value.
   */
  public get displayValue(): string {
    return this.type() === 'currency' || this.type() === 'currency-without-symbol' ? this.formattedValue : this.value();
  }

  /**
   * Updates the value when the input changes.
   * @returns void
   */
  public set _value(value: string) {
    this.value.set(value);
  }

  /**
   * Returns the current value of the input.
   * @returns string
   */
  public get _value(): string {
    return this.value();
  }

  /**
   * Writes a new value to the input.
   * @param value The new value to be written.
   * @returns void
   */
  /**
   * `writeValue` é chamado pelo Angular a partir de qualquer contexto, incluindo
   * durante a deteção de mudanças (o consumer reativo de `refreshView` cobre o
   * template e todos os hooks de ciclo de vida). Escrever num signal aí lança
   * NG0600 e a escrita perde-se — o controlo fica com o valor novo e o input
   * desenhado com o antigo. `untracked` liberta a escrita e evita registar
   * dependências que este método não deve ter.
   */
  public writeValue(value: string | number): void {
    untracked(() => {
      // Convert numeric values to string to ensure consistent handling
      const stringValue = value !== null && value !== undefined ? String(value) : '';
      this._value = stringValue;
      if (this.type() === 'currency' || this.type() === 'currency-without-symbol') {
        this.formattedValue = value ? this.formatAsCurrency(value) : null;
      }
    });
  }

  /**
   * Registers a function to be called when the input changes.
   * @param fn The function to be called when the input changes.
   * @returns void
   */
  public registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  /**
   * Registers a function to be called when the input is touched.
   * @param fn The function to be called when the input is touched.
   * @returns void
   */
  public registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  /**
   * Updates the state of the input when it changes.
   * @param isDisabled A flag indicating if the input is disabled.
   * @returns void
   */
  public setDisabledState?(isDisabled: boolean): void {
    this.state.set(isDisabled ? 'disabled' : this.state());
  }

  /**
   * Focuses the input element and emits the icon clicked event.
   * @returns void
   */
  public clickIcon(): void {
    this.inputElement()?.nativeElement?.focus();
    if (this.clickableIcon()) this.iconClicked.emit();
  }

  /**
   * Marks the input as touched when it loses focus.
   * @returns void
   */
  public barred(): void {
    this.onTouched?.();
    this.validateValue();
    this.updateFormValidity();
    this.blurChange.emit();
  }

  /** Updates the form control validity by propagating the current value */
  private updateFormValidity(): void {
    this.onChange?.(this.value());
  }

  /**
   * Focuses the input element.
   * @returns void
   */
  public focus(): void {
    this.inputElement()?.nativeElement?.focus();
  }

  /**
   * Blurs the input element.
   * @returns void
   */
  public blur(): void {
    this.inputElement()?.nativeElement?.blur();
  }

  /**
   * Emits the focused event.
   * @param event The focus event.
   * @returns void
   */
  public focusChange: OutputEmitterRef<FocusEvent> = output<FocusEvent>();

  /**
   * Emits the input change event.
   * @param event The input change event.
   * @returns void
   */
  public inputChange: OutputEmitterRef<Event> = output<Event>();

  /**
   * Emits the key down event.
   * @param event The key down event.
   * @returns void
   */
  public keyDownChange: OutputEmitterRef<KeyboardEvent> = output<KeyboardEvent>();

  /**
   * Emits the paste event.
   * @param event The paste event.
   * @returns void
   */
  public pasteChange: OutputEmitterRef<ClipboardEvent> = output<ClipboardEvent>();

  /**
   * Focuses the input element and emits the icon clicked event.
   * @param event The focus event.
   * @returns void
   */
  public onFocus(event: FocusEvent): void {
    this.focusChange.emit(event);
  }

  /**
   * Emits the input event.
   * @param event The input event.
   * @returns void
   */
  public onInput(event: Event): void {
    const target = event.target as HTMLInputElement;

    if (this.type() === 'currency' || this.type() === 'currency-without-symbol') {
      const numbersOnly = target.value.replace(/\D/g, '');

      if (numbersOnly === '') {
        this._value = '';
        this.formattedValue = '';
      } else {
        const numericValue = parseInt(numbersOnly, 10) / 100;
        this._value = numericValue.toString();
        this.formattedValue = numericValue ? this.formatAsCurrency(numericValue) : null;
      }

      setTimeout(() => {
        const length = target.value.length;
        target.setSelectionRange(length, length);
      });

      this.inputChange.emit(event);
      this.onChange?.(this.value());
      return;
    }

    this.inputChange.emit(event);
    // Non-currency: parent CVA is updated in onModelChange; calling onChange here can run
    // before the inner NgModel applies the new character and emit stale values.
  }

  /**
   * Handles ngModel changes.
   * @param value The new model value.
   * @returns void
   */
  public onModelChange(value: string): void {
    if (this.type() !== 'currency' && this.type() !== 'currency-without-symbol') {
      this._value = value;
      this.onChange?.(this.value());
    }
  }

  /**
   * Emits the key down event.
   * @param event The key down event.
   * @returns void
   */
  public onKeyDown(event: KeyboardEvent): void {

    if (this.type() === 'currency' || this.type() === 'currency-without-symbol') {
      const allowedKeys = [
        'Backspace', 'Delete', 'Tab', 'Escape', 'Enter',
        'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown',
        'Home', 'End'
      ];

      const isNumericKey = (event.key >= '0' && event.key <= '9');
      const isAllowedKey = allowedKeys.includes(event.key);
      const isCtrlKey = event.ctrlKey || event.metaKey;

      if (!isNumericKey && !isAllowedKey && !isCtrlKey) {
        event.preventDefault();
        return;
      }
    }

    this.keyDownChange.emit(event);
  }

  /**
   * Emits the paste event.
   * @param event The paste event.
   * @returns void
   */
  public onPaste(event: ClipboardEvent): void {
    if (this.type() === 'currency' || this.type() === 'currency-without-symbol') {
      event.preventDefault();

      const pastedText = event.clipboardData?.getData('text') || '';
      const numbersOnly = pastedText.replace(/\D/g, '');

      if (numbersOnly) {
        const target = event.target as HTMLInputElement;
        const currentValue = target.value;
        const selectionStart = target.selectionStart || 0;
        const selectionEnd = target.selectionEnd || 0;

        const newValue = currentValue.substring(0, selectionStart) + numbersOnly + currentValue.substring(selectionEnd);

        target.value = newValue;
        const inputEvent = new Event('input', { bubbles: true });
        target.dispatchEvent(inputEvent);
      }
    }

    this.pasteChange.emit(event);
  }

  /**
   * Observes the input element and sets the max length and min length.
   * @returns void
   */
  private observeInputElement = effect(() => {
    const inputElement = this.inputElement()?.nativeElement;
    const maxLength = this.maxLength();
    const minLength = this.minLength();
    untracked(() => {
      if (!inputElement) return;
      if (typeof maxLength === 'number') inputElement.maxLength = maxLength;
      if (typeof minLength === 'number') inputElement.minLength = minLength;
    });
  });

  /**
   * Validates the value of the input.
   * @returns void
   */
  public validateValue(): void {
    this.errors = {};
    if (typeof this.minLength() === 'number' && this._value?.length < this.minLength()) {
      this.errors['min'] = {
        minlength: this.minLength(),
        actual: this._value?.length
      }
    }

    if (typeof this.maxLength() === 'number' && this._value?.length > this.maxLength()) {
      this.errors['max'] = {
        maxlength: this.maxLength(),
        actual: this._value?.length
      }
    }

    if (this.required() && !this._value) {
      this.errors['required'] = true;
    }

    if (this.type() === 'email' && this._value && !this._value.match(/^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/)) {
      this.errors['email'] = true;
    }

    if(Object.keys(this.errors).length === 0) this.errors = null;
  }
}
